import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Globe, Phone, Bot, TrendingUp, Code2, LifeBuoy, ArrowRight } from 'lucide-react';
import './SolutionsGrid.css';

/* The six pillars, stated on the home page right after the hero. This is the
   proof behind "your business technology, one company" - a visitor should be
   able to see the whole scope without opening the nav. */
const PILLARS = [
  {
    icon: Globe,
    label: 'Website & Commerce',
    blurb: 'Custom sites and storefronts, built and managed.',
    to: '/solutions/websites-commerce'
  },
  {
    icon: Phone,
    label: 'Communications',
    blurb: 'Business lines, routing, and texting that works.',
    to: '/solutions/communications'
  },
  {
    icon: Bot,
    label: 'AI Reception',
    blurb: 'Every call answered, qualified, and booked.',
    to: '/solutions/ai-reception'
  },
  {
    icon: TrendingUp,
    label: 'Search & Growth',
    blurb: 'Local SEO, AI answers, and steady reviews.',
    to: '/solutions/search-growth'
  },
  {
    icon: Code2,
    label: 'Business Software',
    blurb: 'Portals, forms, automations, custom tools.',
    to: '/solutions/business-software'
  },
  {
    icon: LifeBuoy,
    label: 'IT & Support',
    blurb: 'Hosting, monitoring, updates, a real person.',
    to: '/solutions/support'
  }
];

const EASE = [0.22, 0.61, 0.36, 1];

const SolutionsGrid = () => {
  const reduce = useReducedMotion();
  const rise = (d = 0) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 20 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.2 },
          transition: { duration: 0.55, delay: d, ease: EASE }
        };

  return (
    <section className="sg-section" aria-label="What Versa Launch runs for you">
      <div className="sg-inner">
        <motion.p className="sg-eyebrow" {...rise(0)}>Your outsourced technology department</motion.p>
        <motion.h2 className="sg-title" {...rise(0.05)}>
          Six things every business runs on. <span className="sg-gold">One company behind them.</span>
        </motion.h2>

        <div className="sg-grid">
          {PILLARS.map(({ icon: Icon, label, blurb, to }, i) => (
            <motion.div key={to} {...rise(0.06 + (i % 3) * 0.05)}>
              <Link to={to} className="sg-card">
                <span className="sg-card-ic">
                  <Icon size={22} strokeWidth={2.1} aria-hidden="true" />
                </span>
                <span className="sg-card-label">{label}</span>
                <span className="sg-card-blurb">{blurb}</span>
                <span className="sg-card-go" aria-hidden="true">
                  <ArrowRight size={15} strokeWidth={2.4} />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.p className="sg-foot" {...rise(0.2)}>
          Start with one. Add the rest when you are ready.{' '}
          <Link to="/solutions" className="sg-foot-link">
            See all solutions <ArrowRight size={14} strokeWidth={2.6} />
          </Link>
        </motion.p>
      </div>
    </section>
  );
};

export default SolutionsGrid;
